import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package2, 
  Star, 
  Clock, 
  ShieldCheck, 
  CheckCircle2, 
  ArrowLeft,
  Plus,
  Minus,
  ShoppingCart,
  Sparkles,
  Award,
  Users,
  TrendingUp,
  AlertCircle,
  Calendar,
  Heart,
  Share2,
  MapPin,
  ExternalLink
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { FirestoreService } from '../hooks/useFirestoreServices';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { ReviewsList } from '../components/ReviewSystem';
import { AddReviewModal } from '../components/AddReviewModal';
import { generateReviews, getReviewStats } from '../utils/reviewGenerator';

interface PackageDetails {
  id: string;
  name: string;
  description: string;
  serviceIds: string[];
  price: number;
  originalPrice?: number;
  discount?: number;
  savings: number;
  image: string;
  images?: string[];
  popular?: boolean;
  featured?: boolean;
  new?: boolean;
  trending?: boolean;
  isActive?: boolean;
}

export default function PackageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  
  const [packageData, setPackageData] = useState<PackageDetails | null>(null);
  const [includedServices, setIncludedServices] = useState<FirestoreService[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [activeTab, setActiveTab] = useState('overview');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  // Generate reviews for this package
  const generatedReviews = packageData?.id ? generateReviews(packageData.id) : [];
  const reviewStats = generatedReviews.length > 0 ? getReviewStats(generatedReviews) : null;

  useEffect(() => {
    const fetchPackageDetails = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Fetch package details
        const packageDoc = await getDoc(doc(db, 'packages', id));
        
        if (!packageDoc.exists()) {
          console.error('Package not found');
          navigate('/');
          return;
        }
        
        const pkgData = { id: packageDoc.id, ...packageDoc.data() } as PackageDetails;
        
        // Check if package is active
        if (pkgData.isActive === false) {
          console.error('Package is not active');
          navigate('/');
          return;
        }
        
        setPackageData(pkgData);
        
        // Fetch included services
        if (pkgData.serviceIds && pkgData.serviceIds.length > 0) {
          const servicesPromises = pkgData.serviceIds.map(serviceId =>
            getDoc(doc(db, 'services', serviceId))
          );
          
          const servicesSnapshots = await Promise.all(servicesPromises);
          const services = servicesSnapshots
            .filter(snap => snap.exists())
            .map(snap => ({ id: snap.id, ...snap.data() } as FirestoreService));
          
          setIncludedServices(services);
        }
      } catch (error) {
        console.error('Error fetching package details:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPackageDetails();
  }, [id, navigate]);

  const handleAddToCart = () => {
    if (!packageData) return;
    
    // Validate date and time selection
    if (!selectedDate || !selectedTime) {
      setToastType('error');
      setToastMessage('Please select date and time for your booking');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }
    
    addToCart({
      id: packageData.id,
      name: packageData.name,
      price: packageData.price,
      image: packageData.image,
      quantity: quantity,
      date: selectedDate,
      time: selectedTime,
      duration: `${includedServices.length} services`,
      category: 'Package'
    });
    
    setToastType('success');
    setToastMessage('Package added to cart successfully!');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleBookNow = () => {
    if (!user) {
      setToastType('error');
      setToastMessage('Please login to book this package');
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        navigate('/login', { state: { returnPath: `/packages/${id}` } });
      }, 1500);
      return;
    }
    
    // Validate date and time selection
    if (!selectedDate || !selectedTime) {
      setToastType('error');
      setToastMessage('Please select date and time for your booking');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }
    
    handleAddToCart();
    setTimeout(() => navigate('/cart'), 500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center space-y-4"
        >
          <LoadingSpinner />
          <p className="text-gray-600 text-sm md:text-base animate-pulse">
            Loading package details...
          </p>
        </motion.div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Package Not Found</h2>
          <p className="text-gray-600 mb-6">The package you're looking for doesn't exist.</p>
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const allImages = packageData.images || [packageData.image];
  const discount = packageData.discount || 
    (packageData.originalPrice && packageData.originalPrice > packageData.price
      ? parseFloat((((packageData.originalPrice - packageData.price) / packageData.originalPrice) * 100).toFixed(1))
      : 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notification */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-20 right-4 z-50"
          >
            <div className={`flex items-center space-x-3 px-6 py-4 rounded-xl shadow-2xl ${
              toastType === 'error' 
                ? 'bg-red-50 border-2 border-red-200' 
                : 'bg-green-50 border-2 border-green-200'
            }`}>
              {toastType === 'error' ? (
                <AlertCircle className="h-6 w-6 text-red-500" />
              ) : (
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              )}
              <span className={`font-semibold ${
                toastType === 'error' ? 'text-red-800' : 'text-green-800'
              }`}>{toastMessage}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        {/* Main Grid Layout - Similar to Service Detail Page */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="relative aspect-[16/10]"
              >
                <img
                  src={allImages[selectedImage]}
                  alt={packageData.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Floating Badges */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <span className="bg-gradient-to-r from-primary-600 to-primary-700 text-white text-sm px-4 py-2 rounded-full font-bold shadow-lg flex items-center gap-2">
                    <Package2 className="h-4 w-4" />
                    Package Deal
                  </span>
                  {packageData.popular && (
                    <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 text-sm px-4 py-2 rounded-full font-bold shadow-lg flex items-center gap-2">
                      <Star className="h-4 w-4 fill-current" />
                      Popular
                    </span>
                  )}
                  {packageData.trending && (
                    <span className="bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm px-4 py-2 rounded-full font-bold shadow-lg flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Trending
                    </span>
                  )}
                </div>

                {discount > 0 && (
                  <div className="absolute top-4 right-4">
                    <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      {discount}% OFF
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Thumbnail Gallery */}
              {allImages.length > 1 && (
                <div className="grid grid-cols-5 gap-2 p-4 bg-gray-50">
                  {allImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`relative rounded-lg overflow-hidden aspect-square transition-all ${
                        selectedImage === index
                          ? 'ring-4 ring-primary-500 scale-105'
                          : 'ring-2 ring-gray-200 hover:ring-gray-300 hover:scale-105'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${packageData.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Package Info Card */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              {/* Header with Price */}
              <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-6">
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                    {packageData.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Package2 className="h-4 w-4 text-primary-600" />
                      <span>{includedServices.length} Services</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-green-600" />
                      <span>Quality Assured</span>
                    </div>
                  </div>
                </div>
                
                {/* Pricing */}
                <div className="flex flex-col items-start md:items-end mt-4 md:mt-0 bg-primary-50 md:bg-transparent p-3 md:p-0 rounded-lg">
                  <div className="text-3xl font-bold text-primary-600">
                    ₹{packageData.price.toLocaleString('en-IN')}
                  </div>
                  {packageData.originalPrice && packageData.originalPrice > packageData.price && (
                    <div className="flex items-center gap-2">
                      <span className="text-lg text-gray-400 line-through">
                        ₹{packageData.originalPrice.toLocaleString('en-IN')}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full font-medium">
                        {discount}% OFF
                      </span>
                    </div>
                  )}
                  {packageData.savings > 0 && (
                    <p className="text-sm text-green-600 font-medium mt-1">
                      Save ₹{packageData.savings.toLocaleString('en-IN')}
                    </p>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  {[
                    { id: 'overview', name: 'Overview' },
                    { id: 'services', name: 'Included Services' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-3 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.name}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <p className="text-gray-700 leading-relaxed">
                      {packageData.description}
                    </p>
                  </div>

                  {/* Why Choose This Package */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Why Choose This Package?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                        <div className="bg-green-100 p-2 rounded-lg inline-block mb-3">
                          <CheckCircle2 className="h-6 w-6 text-green-600" />
                        </div>
                        <h4 className="font-bold text-gray-900 mb-1">Best Value</h4>
                        <p className="text-sm text-gray-600">
                          Save up to {discount}% compared to individual bookings
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-xl p-4 border border-primary-100">
                        <div className="bg-primary-100 p-2 rounded-lg inline-block mb-3">
                          <Award className="h-6 w-6 text-primary-600" />
                        </div>
                        <h4 className="font-bold text-gray-900 mb-1">Quality Assured</h4>
                        <p className="text-sm text-gray-600">
                          Verified professionals for all services
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-secondary-50 to-purple-50 rounded-xl p-4 border border-secondary-100">
                        <div className="bg-secondary-100 p-2 rounded-lg inline-block mb-3">
                          <Users className="h-6 w-6 text-secondary-600" />
                        </div>
                        <h4 className="font-bold text-gray-900 mb-1">Convenient</h4>
                        <p className="text-sm text-gray-600">
                          Book multiple services at once
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'services' && (
                <div className="space-y-4">
                  <p className="text-gray-600 mb-6">
                    This package includes {includedServices.length} premium services. Click on any service to view full details.
                  </p>
                  {includedServices.length > 0 ? (
                    <div className="space-y-4">
                      {includedServices.map((service, index) => (
                        <Link
                          key={service.id}
                          to={`/services/${service.id}`}
                          className="block group"
                        >
                          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border-2 border-gray-100 hover:border-primary-300 hover:shadow-lg transition-all">
                            <div className="flex gap-4">
                              {/* Service Number Badge */}
                              <div className="flex-shrink-0 relative">
                                <img
                                  src={service.image}
                                  alt={service.name}
                                  className="w-24 h-24 rounded-xl object-cover shadow-md group-hover:shadow-lg transition-shadow"
                                />
                                <div className="absolute -top-2 -right-2 bg-primary-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shadow-lg">
                                  {index + 1}
                                </div>
                              </div>
                              
                              {/* Service Details */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors mb-2 flex items-center gap-2">
                                      {service.name}
                                      <ExternalLink className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </h3>
                                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                                      {service.description}
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Service Meta */}
                                <div className="flex flex-wrap items-center gap-4 mb-3">
                                  <div className="flex items-center gap-1.5 text-sm">
                                    <Clock className="h-4 w-4 text-gray-500" />
                                    <span className="text-gray-700 font-medium">{service.duration}</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 text-sm">
                                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                                    <span className="text-gray-700 font-medium">{service.rating}</span>
                                    {service.reviewCount && (
                                      <span className="text-gray-500">({service.reviewCount})</span>
                                    )}
                                  </div>
                                  {service.location && (
                                    <div className="flex items-center gap-1.5 text-sm">
                                      <MapPin className="h-4 w-4 text-gray-500" />
                                      <span className="text-gray-600">{service.location}</span>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Service Features */}
                                {service.includes && service.includes.length > 0 && (
                                  <div className="space-y-1.5">
                                    {service.includes.slice(0, 2).map((item, idx) => (
                                      <div key={idx} className="flex items-start gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                                        <span className="text-sm text-gray-600 line-clamp-1">{item}</span>
                                      </div>
                                    ))}
                                    {service.includes.length > 2 && (
                                      <p className="text-sm text-primary-600 font-medium ml-6">
                                        +{service.includes.length - 2} more features
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Package2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No services information available</p>
                    </div>
                  )}
                </div>
              )}

            </div>

            {/* Reviews Section - Outside tabs, at the bottom */}
            <div className="bg-white rounded-xl md:rounded-2xl shadow-sm p-4 md:p-6 mt-4 md:mt-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-4 border-b border-gray-200 mb-4 md:mb-6">
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900">Customer Reviews</h3>
                  <p className="text-xs md:text-sm text-gray-500 mt-1">
                    {reviewStats?.totalReviews || 0} reviews • {reviewStats?.avgRating || 5} average rating
                  </p>
                </div>
                {user && (
                  <button
                    onClick={() => setShowReviewModal(true)}
                    className="px-3 py-2 md:px-4 md:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all flex items-center gap-1.5 md:gap-2 font-medium text-sm md:text-base whitespace-nowrap"
                  >
                    <Star className="h-3.5 w-3.5 md:h-4 md:w-4" />
                    Write Review
                  </button>
                )}
              </div>
              {generatedReviews.length > 0 && (
                <div className="space-y-6">
                  {generatedReviews.slice(0, 10).map((review) => (
                    <div key={review.id} className="border-b border-gray-100 last:border-0 pb-6 last:pb-0">
                      <div className="flex items-start gap-4">
                        {review.userAvatar ? (
                          <img 
                            src={review.userAvatar} 
                            alt={review.userName}
                            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-gray-600 font-semibold text-lg">
                              {review.userName.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <div>
                              <h4 className="font-semibold text-gray-900">{review.userName}</h4>
                              <p className="text-sm text-gray-500">{review.location}</p>
                            </div>
                            <span className="text-xs text-gray-400 flex-shrink-0">{review.date}</span>
                          </div>
                          <div className="flex items-center gap-1 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`h-4 w-4 ${
                                  i < review.rating 
                                    ? 'fill-yellow-400 text-yellow-400' 
                                    : 'text-gray-300'
                                }`} 
                              />
                            ))}
                          </div>
                          <p className="text-gray-700 text-sm">{review.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Booking Card (Sticky) */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-xl p-6 sticky top-24"
            >
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Book This Package</h2>
              
              {!user ? (
                <div className="text-center py-4 md:py-6">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">Please Login First</h3>
                  <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4">You need to be logged in to book this package</p>
                  <button
                    onClick={() => {
                      navigate('/login', { state: { returnPath: `/packages/${id}` } });
                    }}
                    className="inline-block bg-primary-600 text-white px-5 py-2 md:px-6 md:py-2.5 rounded-lg hover:bg-primary-700 transition-colors text-sm md:text-base"
                  >
                    Login / Sign Up
                  </button>
                </div>
              ) : (
                <>
                  {/* Number of People */}
                  <div className="mb-4 md:mb-6">
                    <label className="block text-sm md:text-base font-semibold text-primary-700 mb-2 md:mb-3">
                      Number of People
                    </label>
                    <div className="flex items-center space-x-3 md:space-x-4">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="p-2 border border-gray-300 rounded-lg hover:border-primary-300"
                      >
                        <Minus className="h-3.5 w-3.5 md:h-4 md:w-4" />
                      </button>
                      <span className="text-base md:text-lg font-semibold px-3 md:px-4">{quantity}</span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="p-2 border border-gray-300 rounded-lg hover:border-primary-300"
                      >
                        <Plus className="h-3.5 w-3.5 md:h-4 md:w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Date Selection */}
                  <div className="mb-4 md:mb-6">
                    <label className="block text-sm md:text-base font-semibold text-primary-700 mb-2 md:mb-3">
                      Select Date *
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        required
                        className="w-full px-3 py-3 md:px-4 md:py-3.5 pr-3 md:pr-4 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 bg-white cursor-pointer text-sm md:text-base font-semibold"
                        style={{
                          colorScheme: 'light'
                        }}
                        onFocus={(e) => {
                          e.target.showPicker?.();
                        }}
                      />
                    </div>
                    {!selectedDate && (
                      <p className="text-xs text-gray-500 mt-1">Please select your preferred service date</p>
                    )}
                  </div>

                  {/* Time Selection */}
                  <div className="mb-4 md:mb-6">
                    <label className="block text-sm md:text-base font-semibold text-primary-700 mb-2 md:mb-3">
                      Select Time *
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-primary-600 pointer-events-none z-10" />
                      <input
                        type="time"
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        required
                        min="08:00"
                        max="20:00"
                        step="900"
                        className="w-full px-3 py-3 md:px-4 md:py-3.5 pl-10 md:pl-12 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 bg-white cursor-pointer text-base md:text-lg font-medium hover:border-primary-400 transition-colors"
                        style={{
                          colorScheme: 'light'
                        }}
                      />
                    </div>
                    <div className="mt-2 flex items-start gap-2">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p className="text-xs text-gray-600">
                        <span className="font-semibold text-gray-700">Select time:</span> Click the field and choose from the time picker, or type directly (e.g., 14:30 for 2:30 PM). Available <span className="font-semibold">8:00 AM - 8:00 PM</span> in 15-min intervals.
                      </p>
                    </div>
                  </div>

                  {/* Price Breakdown */}
                  <div className="border-t pt-4 mb-6">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Package ({quantity}x)</span>
                        <span>₹{packageData.price * quantity}</span>
                      </div>
                    </div>
                    
                    <div className="border-t mt-4 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold">Total</span>
                        <span className="text-2xl font-bold text-primary-600">₹{(packageData.price * quantity).toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {includedServices.length} Services Included
                      </div>
                    </div>
                  </div>

                  {/* Booking Actions */}
                  <div className="space-y-3">
                    <button 
                      onClick={handleBookNow}
                      className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold"
                    >
                      Buy Now
                    </button>
                    <button 
                      onClick={handleAddToCart}
                      className="w-full bg-white text-primary-600 border-2 border-primary-600 py-3 rounded-lg hover:bg-primary-50 transition-colors font-semibold"
                    >
                      Add to Cart
                    </button>
                  </div>
                </>
              )}

              {/* Trust Indicators */}
              <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>Verified and trusted professionals</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>100% satisfaction guarantee</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>Flexible rescheduling options</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <AddReviewModal
          serviceId={packageData.id}
          serviceName={packageData.name}
          onClose={() => setShowReviewModal(false)}
          onSuccess={() => {
            setShowReviewModal(false);
            setToastType('success');
            setToastMessage('Review submitted successfully!');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
          }}
        />
      )}
    </div>
  );
}
